import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import {
  Subscription,
  SubscriptionPlan,
  FeatureKey,
  IELTSExamType,
  FEATURE_ACCESS,
  BETA_FEATURE_ACCESS,
  BETA_LIMITS,
  FREE_TIER_DAILY_LIMIT,
  FREE_TIER_DAILY_INTERVIEW_LIMIT,
  MAX_DAILY_BONUS_CREDITS,
  PricingRegion,
  BillingCycle,
} from '../types/subscription';
import CancellationSurveyModal from '../components/CancellationSurveyModal';
import { trackCancellation } from '../services/analyticsService';
import { isEarlyBirdActive, EARLY_BIRD_EVENT } from '../config/earlyBirdEvent';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5555';

// Map legacy plan types to current valid plans
// This ensures backward compatibility for existing subscriptions in Firestore
const normalizePlan = (plan: string): SubscriptionPlan => {
  const legacyPlanMapping: Record<string, SubscriptionPlan> = {
    'pro_interview': 'pro',
    'ielts': 'pro',
    'pro_ielts': 'pro',
    'interview': 'pro',
    'interview_pro': 'pro',
  };
  return legacyPlanMapping[plan] || (plan as SubscriptionPlan);
};

interface SubscriptionContextType {
  subscription: Subscription | null;
  plan: SubscriptionPlan;
  isActive: boolean;
  loading: boolean;
  error: string | null;
  canAccess: (feature: FeatureKey) => boolean;
  getRecordingsRemaining: () => number;
  checkAndIncrementRecording: () => Promise<{ allowed: boolean; remaining: number; error?: string }>;
  createStripeCheckout: (plan: SubscriptionPlan, billingCycle: BillingCycle, successUrl: string, cancelUrl: string) => Promise<string>;
  createStripePortal: (returnUrl: string) => Promise<string>;
  refreshSubscription: () => Promise<void>;
  initializeSubscription: (region?: PricingRegion) => Promise<void>;
  // Beta user support
  isBetaUser: boolean;
  canUseInterview: () => boolean;
  canUseHellMode: () => boolean;
  canUseIeltsExam: (examType: IELTSExamType) => boolean;
  canUseFullIelts: () => boolean;
  trackBetaInterviewUse: () => Promise<boolean>;
  trackBetaIeltsUse: (examType: IELTSExamType) => Promise<boolean>;
  // Tetris bonus credits
  bonusCredits: number;
  canEarnBonusCredits: () => boolean;
  addBonusCredits: (credits: number) => Promise<boolean>;
  // Daily interview limit for free users
  remainingInterviews: number;
  trackInterviewUse: () => Promise<boolean>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

interface SubscriptionProviderProps {
  children: ReactNode;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showCancellationSurvey, setShowCancellationSurvey] = useState(false);

  // Derived state - normalize legacy plan types to current valid plans
  const plan: SubscriptionPlan = subscription?.plan ? normalizePlan(subscription.plan) : 'free';
  const isActive = subscription?.status === 'active' || subscription?.status === 'trialing';

  // Beta user support
  const isBetaUser = subscription?.isBeta ?? false;
  const bonusCredits = subscription?.bonusCredits ?? 0;

  // Fetch subscription data
  const fetchSubscription = useCallback(async () => {
    if (!currentUser) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    try {
      setError(null);
      const response = await fetch(`${API_BASE_URL}/api/subscriptions/${currentUser.uid}`);

      if (!response.ok) {
        throw new Error('Failed to fetch subscription');
      }

      const data = await response.json();

      // Convert date strings to Date objects
      setSubscription({
        ...data,
        currentPeriodStart: data.currentPeriodStart ? new Date(data.currentPeriodStart) : new Date(),
        currentPeriodEnd: data.currentPeriodEnd ? new Date(data.currentPeriodEnd) : new Date(),
        lastRecordingDate: data.lastRecordingDate ? new Date(data.lastRecordingDate) : null,
        createdAt: data.createdAt ? new Date(data.createdAt) : new Date(),
        updatedAt: data.updatedAt ? new Date(data.updatedAt) : new Date(),
      });
    } catch (err) {
      console.error('Error fetching subscription:', err);
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Initialize subscription on auth change
  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Check if user needs to provide cancellation feedback
  useEffect(() => {
    const checkCancellationFeedback = async () => {
      if (!currentUser) return;

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/subscriptions/${currentUser.uid}/cancellation-feedback`
        );
        if (response.ok) {
          const data = await response.json();
          if (data.needsFeedback) {
            setShowCancellationSurvey(true);
          }
        }
      } catch (err) {
        console.error('Error checking cancellation feedback:', err);
      }
    };

    checkCancellationFeedback();
  }, [currentUser]);

  // Handle cancellation survey submit
  const handleCancellationFeedbackSubmit = async (reason: string, feedback: string) => {
    if (!currentUser) return;

    try {
      // Submit to analytics
      await trackCancellation(
        currentUser.uid,
        currentUser.email || '',
        subscription?.plan || 'unknown',
        reason,
        feedback
      );

      // Clear the flag on the server
      await fetch(`${API_BASE_URL}/api/subscriptions/${currentUser.uid}/cancellation-feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason, feedback, skipped: false }),
      });

      setShowCancellationSurvey(false);
    } catch (err) {
      console.error('Error submitting cancellation feedback:', err);
      setShowCancellationSurvey(false);
    }
  };

  // Handle cancellation survey skip
  const handleCancellationFeedbackSkip = async () => {
    if (!currentUser) return;

    try {
      // Clear the flag on the server
      await fetch(`${API_BASE_URL}/api/subscriptions/${currentUser.uid}/cancellation-feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ skipped: true }),
      });

      setShowCancellationSurvey(false);
    } catch (err) {
      console.error('Error skipping cancellation feedback:', err);
      setShowCancellationSurvey(false);
    }
  };

  // Check if user can access a feature
  const canAccess = useCallback((feature: FeatureKey): boolean => {
    if (!isActive) return false;

    // Beta users have special access
    if (isBetaUser) {
      return BETA_FEATURE_ACCESS.includes(feature);
    }

    // Early Bird Event: Free users get full access to all features
    if (isEarlyBirdActive() && plan === 'free') {
      return (EARLY_BIRD_EVENT.grantedFeatures as readonly string[]).includes(feature);
    }

    return FEATURE_ACCESS[plan].includes(feature);
  }, [plan, isActive, isBetaUser]);

  // Get remaining recordings for free tier
  const getRecordingsRemaining = useCallback((): number => {
    if (plan !== 'free') return -1; // Unlimited

    // Early Bird Event: Free users get unlimited recordings
    if (isEarlyBirdActive()) return -1;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const lastDate = subscription?.lastRecordingDate;
    const bonusCredits = subscription?.bonusCredits || 0;
    const totalAvailable = FREE_TIER_DAILY_LIMIT + bonusCredits;

    if (!lastDate || lastDate < today) {
      return totalAvailable;
    }

    return Math.max(0, totalAvailable - (subscription?.dailyRecordings || 0));
  }, [plan, subscription]);

  // Check and increment recording count
  const checkAndIncrementRecording = useCallback(async (): Promise<{ allowed: boolean; remaining: number; error?: string }> => {
    if (!currentUser) {
      return { allowed: false, remaining: 0, error: 'Not logged in' };
    }

    // Paid plans have unlimited recordings
    if (plan !== 'free') {
      return { allowed: true, remaining: -1 };
    }

    // Early Bird Event: Free users get unlimited recordings
    if (isEarlyBirdActive()) {
      return { allowed: true, remaining: -1 };
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/subscriptions/${currentUser.uid}/record`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error('Failed to check recording limit');
      }

      const result = await response.json();

      // Refresh subscription to get updated count
      await fetchSubscription();

      // Map server response (success, remainingRecordings) to client format (allowed, remaining)
      return {
        allowed: result.success,
        remaining: result.remainingRecordings,
      };
    } catch (err) {
      console.error('Error checking recording limit:', err);
      // Return allowed: true on network errors to let users try recording
      // The actual recording will fail if there's a real server issue
      return { allowed: true, remaining: -1, error: 'network_error' };
    }
  }, [currentUser, plan, fetchSubscription]);

  // Create Stripe checkout session
  const createStripeCheckout = useCallback(async (
    plan: SubscriptionPlan,
    billingCycle: BillingCycle,
    successUrl: string,
    cancelUrl: string
  ): Promise<string> => {
    if (!currentUser || !currentUser.email) {
      throw new Error('User must be logged in');
    }

    const response = await fetch(`${API_BASE_URL}/api/payments/stripe/create-checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        plan,
        billingCycle,
        userId: currentUser.uid,
        userEmail: currentUser.email,
        successUrl,
        cancelUrl,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create checkout session');
    }

    const { url } = await response.json();
    return url;
  }, [currentUser]);

  // Create Stripe customer portal session
  const createStripePortal = useCallback(async (returnUrl: string): Promise<string> => {
    if (!subscription?.stripeCustomerId) {
      throw new Error('No Stripe customer found');
    }

    const response = await fetch(`${API_BASE_URL}/api/payments/stripe/create-portal`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        customerId: subscription.stripeCustomerId,
        returnUrl,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create portal session');
    }

    const { url } = await response.json();
    return url;
  }, [subscription]);

  // Refresh subscription data
  const refreshSubscription = useCallback(async (): Promise<void> => {
    setLoading(true);
    await fetchSubscription();
  }, [fetchSubscription]);

  // Initialize subscription for new user
  const initializeSubscription = useCallback(async (region: PricingRegion = 'ca'): Promise<void> => {
    if (!currentUser || !currentUser.email) {
      throw new Error('User must be logged in');
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/subscriptions/${currentUser.uid}/initialize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: currentUser.email,
          region,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to initialize subscription');
      }

      await fetchSubscription();
    } catch (err) {
      console.error('Error initializing subscription:', err);
      throw err;
    }
  }, [currentUser, fetchSubscription]);

  // Calculate remaining interviews for free users (includes bonus credits)
  const getRemainingInterviews = useCallback((): number => {
    if (!subscription) return 0;

    // Pro users and users with interview access have unlimited
    if (canAccess('interview_unlimited_questions') || canAccess('interview_practice')) {
      return Infinity;
    }

    // Beta users have limited sessions total
    if (isBetaUser) {
      return Math.max(0, BETA_LIMITS.maxInterviewSessions - (subscription.betaInterviewsUsed || 0));
    }

    // Free users: check daily limit + bonus credits
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const lastDate = subscription.lastInterviewDate ? new Date(subscription.lastInterviewDate) : null;
    const isNewDay = !lastDate || lastDate < today;
    const dailyUsed = isNewDay ? 0 : (subscription.dailyInterviews || 0);
    const totalAvailable = FREE_TIER_DAILY_INTERVIEW_LIMIT + (subscription.bonusCredits || 0);

    return Math.max(0, totalAvailable - dailyUsed);
  }, [subscription, canAccess, isBetaUser]);

  const remainingInterviews = getRemainingInterviews();

  // Beta-specific checks
  const canUseInterview = useCallback((): boolean => {
    if (!subscription) return false;

    // Pro users have unlimited access
    if (canAccess('interview_unlimited_questions')) return true;

    // Beta users have limited access
    if (isBetaUser) {
      return (subscription.betaInterviewsUsed || 0) < BETA_LIMITS.maxInterviewSessions;
    }

    // Paid users with interview feature
    if (canAccess('interview_practice')) return true;

    // Free users: check daily limit + bonus credits
    return remainingInterviews > 0;
  }, [subscription, canAccess, isBetaUser, remainingInterviews]);

  const canUseHellMode = useCallback((): boolean => {
    if (!subscription) return false;

    // Beta users cannot use hell mode
    if (isBetaUser) return false;

    return canAccess('interview_advanced_feedback');
  }, [subscription, canAccess, isBetaUser]);

  const canUseIeltsExam = useCallback((examType: IELTSExamType): boolean => {
    if (!subscription) return false;

    // Pro users have unlimited access
    if (canAccess('ielts_full_duration')) return true;

    // All non-pro users (free, basic, beta) have limited access - 1 short test per exam type
    const usedExams = subscription.betaIeltsExamsUsed || [];
    return !usedExams.includes(examType);
  }, [subscription, canAccess]);

  const canUseFullIelts = useCallback((): boolean => {
    if (!subscription) return false;

    // Only pro users can use full duration
    return canAccess('ielts_full_duration');
  }, [subscription, canAccess]);

  // Track beta interview usage
  const trackBetaInterviewUse = useCallback(async (): Promise<boolean> => {
    if (!currentUser || !isBetaUser) return true; // Non-beta users don't need tracking

    try {
      const response = await fetch(`${API_BASE_URL}/api/subscriptions/${currentUser.uid}/beta-interview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error('Failed to track beta interview usage');
      }

      const result = await response.json();
      if (result.success) {
        setSubscription((prev) =>
          prev
            ? {
                ...prev,
                betaInterviewsUsed: result.betaInterviewsUsed,
              }
            : null
        );
      }
      return result.success;
    } catch (err) {
      console.error('Error tracking beta interview use:', err);
      return false;
    }
  }, [currentUser, isBetaUser]);

  // Track IELTS usage for non-pro users
  const trackBetaIeltsUse = useCallback(async (examType: IELTSExamType): Promise<boolean> => {
    // Pro users don't need tracking (unlimited access)
    if (!currentUser || canAccess('ielts_full_duration')) return true;

    try {
      const response = await fetch(`${API_BASE_URL}/api/subscriptions/${currentUser.uid}/beta-ielts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ examType }),
      });

      if (!response.ok) {
        throw new Error('Failed to track beta IELTS usage');
      }

      const result = await response.json();
      if (result.success) {
        setSubscription((prev) =>
          prev
            ? {
                ...prev,
                betaIeltsExamsUsed: result.betaIeltsExamsUsed,
              }
            : null
        );
      }
      return result.success;
    } catch (err) {
      console.error('Error tracking beta IELTS use:', err);
      return false;
    }
  }, [currentUser, canAccess]);

  // Check if user can earn more bonus credits today
  const canEarnBonusCredits = useCallback((): boolean => {
    if (!subscription) return false;

    // Only basic users (or users without unlimited recordings) can earn bonus credits
    if (canAccess('unlimited_recordings')) return false;

    // Check if already at max daily bonus credits
    const today = new Date().toDateString();
    const lastBonusDate = subscription.lastBonusCreditDate
      ? new Date(subscription.lastBonusCreditDate).toDateString()
      : null;

    // Reset bonus credits if new day
    if (lastBonusDate !== today) {
      return true;
    }

    return (subscription.bonusCredits || 0) < MAX_DAILY_BONUS_CREDITS;
  }, [subscription, canAccess]);

  // Add bonus credits from Tetris game
  const addBonusCredits = useCallback(async (credits: number): Promise<boolean> => {
    if (!currentUser || !subscription) return false;

    if (!canEarnBonusCredits()) return false;

    try {
      const response = await fetch(`${API_BASE_URL}/api/subscriptions/${currentUser.uid}/bonus-credits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credits }),
      });

      if (!response.ok) {
        throw new Error('Failed to add bonus credits');
      }

      const result = await response.json();
      if (result.success) {
        setSubscription((prev) =>
          prev
            ? {
                ...prev,
                bonusCredits: result.bonusCredits,
                lastBonusCreditDate: new Date(),
              }
            : null
        );
      }
      return result.success;
    } catch (err) {
      console.error('Error adding bonus credits:', err);
      return false;
    }
  }, [currentUser, subscription, canEarnBonusCredits]);

  // Track daily interview usage for free users
  const trackInterviewUse = useCallback(async (): Promise<boolean> => {
    if (!currentUser || !subscription) return false;

    // Pro users and paid users don't need tracking
    if (canAccess('interview_unlimited_questions') || canAccess('interview_practice')) {
      return true;
    }

    // Beta users use beta tracking
    if (isBetaUser) {
      return trackBetaInterviewUse();
    }

    // Free users: track daily interview usage
    try {
      const response = await fetch(`${API_BASE_URL}/api/subscriptions/${currentUser.uid}/interview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error('Failed to track interview usage');
      }

      const result = await response.json();
      if (result.success) {
        setSubscription((prev) =>
          prev
            ? {
                ...prev,
                dailyInterviews: result.dailyInterviews,
                lastInterviewDate: new Date(),
                bonusCredits: result.bonusCreditsUsed
                  ? Math.max(0, (prev.bonusCredits || 0) - 1)
                  : prev.bonusCredits,
              }
            : null
        );
      }
      return result.success;
    } catch (err) {
      console.error('Error tracking interview use:', err);
      return false;
    }
  }, [currentUser, subscription, canAccess, isBetaUser, trackBetaInterviewUse]);

  const value: SubscriptionContextType = {
    subscription,
    plan,
    isActive,
    loading,
    error,
    canAccess,
    getRecordingsRemaining,
    checkAndIncrementRecording,
    createStripeCheckout,
    createStripePortal,
    refreshSubscription,
    initializeSubscription,
    // Beta user support
    isBetaUser,
    canUseInterview,
    canUseHellMode,
    canUseIeltsExam,
    canUseFullIelts,
    trackBetaInterviewUse,
    trackBetaIeltsUse,
    // Tetris bonus credits
    bonusCredits,
    canEarnBonusCredits,
    addBonusCredits,
    // Daily interview limit for free users
    remainingInterviews,
    trackInterviewUse,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
      {showCancellationSurvey && (
        <CancellationSurveyModal
          onSubmit={handleCancellationFeedbackSubmit}
          onSkip={handleCancellationFeedbackSkip}
        />
      )}
    </SubscriptionContext.Provider>
  );
};
